import React, { useEffect, useState } from 'react';
import { proCaseApi, ProCase } from './proCaseApi';
import { API_BASE } from '../../services/api';
import { ReportBody } from '../results/ReportBody';
import { TrustEstateInclusionNotice } from '../results/TrustEstateInclusionNotice';
import { Button } from '../ui';
import { postAudit } from '../probateApi';
import { generateAndOpenProbateReport } from '../utils/generateProbateReport';
import { ProbateResponse, EstateAssets, EstateProfile } from '../../types';
import { Printer, ArrowLeft, Pencil } from 'lucide-react';

// ---------------------------------------------------------------------------
// /pro/cases/:caseId/report — read-only Pro report view
// ---------------------------------------------------------------------------
// Loads the case + linked estate, runs the audit endpoint to compute the
// report, and renders ReportBody directly. Does NOT mount ProbateWizard or
// ReviewStep, so there is no save button, no step rail, no inline edit
// affordances. Pro reports are not gated by Stripe — the audit endpoint
// returns full results for any authenticated user (the consumer free-tier
// redaction kicks in only when the estate is in 'draft' AND the user has
// no purchase, which is fine for Pro since Pro estates are owned by a Pro
// user; consumer billing logic does not apply).
//
// Edit affordance: a top-bar "Edit audit" button that navigates to
// /pro/cases/:caseId/audit. The user has to explicitly leave the report
// view to make changes — they cannot edit from within it.
// ---------------------------------------------------------------------------

interface Props {
    caseId: string;
    onBack: () => void;
    onEdit: () => void;
}

interface EstateLite {
    id: string;
    wizard_data_json: { EstateProfile?: EstateProfile; EstateAssets?: EstateAssets } & Record<string, any>;
    selected_categories_json?: string[];
    status: 'draft' | 'unlocked';
}

async function loadEstate(estateId: string): Promise<EstateLite> {
    const r = await fetch(`${API_BASE}/api/estates/${estateId}`, { credentials: 'include' });
    if (!r.ok) throw new Error(`Failed to load estate (${r.status})`);
    return r.json();
}

const fmtPreparedDate = (d: Date) =>
    d.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });

export const ProCaseReportView: React.FC<Props> = ({ caseId, onBack, onEdit }) => {
    const [proCase, setProCase] = useState<ProCase | null>(null);
    const [estate, setEstate] = useState<EstateLite | null>(null);
    const [audit, setAudit] = useState<ProbateResponse | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);
    const [exporting, setExporting] = useState(false);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const c = await proCaseApi.get(caseId);
                if (cancelled) return;
                setProCase(c);

                if (!c.estate_id) {
                    setLoading(false);
                    return;
                }

                const e = await loadEstate(c.estate_id);
                if (cancelled) return;
                setEstate(e);

                const profile = e.wizard_data_json?.EstateProfile ?? {};
                const assets = e.wizard_data_json?.EstateAssets ?? {};
                const { data, meta } = await postAudit({
                    EstateProfile: profile,
                    EstateAssets: assets,
                }, '', e.id);
                if (cancelled) return;
                if (!data) {
                    throw new Error(meta.error || 'Audit could not be computed');
                }
                setAudit(data);
            } catch (err: any) {
                if (!cancelled) setError(err.message || 'Failed to load report');
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [caseId]);

    const handlePrint = async () => {
        if (!estate || !audit) return;
        setExporting(true);
        try {
            const profile = (estate.wizard_data_json?.EstateProfile ?? {}) as EstateProfile;
            const assets = (estate.wizard_data_json?.EstateAssets ?? {}) as EstateAssets;
            generateAndOpenProbateReport(
                { EstateProfile: profile, EstateAssets: assets },
                audit,
                proCase
                    ? {
                        client_name: proCase.client_name,
                        deceased_name: proCase.deceased_name,
                        executor_name: proCase.executor_name,
                        matter_reference: proCase.matter_reference,
                        prepared_date: new Date(),
                    }
                    : null,
            );
        } catch (err) {
            console.error('[pro/report] export failed:', err);
        } finally {
            setExporting(false);
        }
    };

    // Header: professional metadata, always shown above any state.
    const Header = (
        <header className="border-b border-rule bg-white no-print">
            <div className="max-w-[960px] mx-auto px-8 py-4 flex items-start justify-between gap-6">
                <div className="min-w-0">
                    <button
                        onClick={onBack}
                        className="text-[12px] text-ink3 hover:text-ink flex items-center gap-1.5 mb-2"
                    >
                        <ArrowLeft className="w-3.5 h-3.5" /> Back to case
                    </button>
                    <h1 className="font-serif text-[22px] text-ink leading-tight">
                        {proCase?.client_name || proCase?.deceased_name || 'Probate report'}
                    </h1>
                    <dl className="mt-2 grid grid-cols-[auto_1fr] gap-x-4 gap-y-0.5 text-[11px] text-ink3">
                        {proCase?.client_name && (<><dt className="tracking-eyebrow uppercase">Client</dt><dd className="text-ink">{proCase.client_name}</dd></>)}
                        {proCase?.deceased_name && (<><dt className="tracking-eyebrow uppercase">Deceased</dt><dd className="text-ink">{proCase.deceased_name}</dd></>)}
                        {proCase?.executor_name && (<><dt className="tracking-eyebrow uppercase">Executor</dt><dd className="text-ink">{proCase.executor_name}</dd></>)}
                        {proCase?.matter_reference && (<><dt className="tracking-eyebrow uppercase">Matter ref</dt><dd className="text-ink">{proCase.matter_reference}</dd></>)}
                        <dt className="tracking-eyebrow uppercase">Prepared</dt>
                        <dd className="text-ink">{fmtPreparedDate(new Date())}</dd>
                    </dl>
                </div>
                <div className="flex flex-col gap-2 shrink-0">
                    <Button variant="secondary" onClick={onEdit} icon={<Pencil className="w-3.5 h-3.5" />}>
                        Edit audit
                    </Button>
                    <Button
                        variant="ghost"
                        onClick={handlePrint}
                        disabled={!audit || exporting}
                        icon={<Printer className="w-3.5 h-3.5" />}
                    >
                        {exporting ? 'Preparing…' : 'Print / export'}
                    </Button>
                </div>
            </div>
        </header>
    );

    // ── No estate yet — direct user to audit ──
    if (!loading && proCase && !proCase.estate_id) {
        return (
            <div>
                {Header}
                <main className="max-w-[640px] mx-auto px-8 py-12">
                    <p className="font-serif italic text-[14px] text-ink2 leading-relaxed pl-4 border-l-2 border-accent max-w-[520px]">
                        No audit data yet for this case. Start the audit to generate a report.
                    </p>
                    <div className="mt-6">
                        <Button variant="primary" onClick={onEdit}>Start audit</Button>
                    </div>
                </main>
            </div>
        );
    }

    if (error) {
        return (
            <div>
                {Header}
                <main className="max-w-[640px] mx-auto px-8 py-12">
                    <p className="text-[13px] text-red-600">{error}</p>
                </main>
            </div>
        );
    }

    if (loading || !audit || !estate) {
        return (
            <div>
                {Header}
                <main className="max-w-[640px] mx-auto px-8 py-12">
                    <p className="text-[13px] text-ink3">Generating report…</p>
                </main>
            </div>
        );
    }

    const profile = (estate.wizard_data_json?.EstateProfile ?? {}) as EstateProfile;
    const assets = (estate.wizard_data_json?.EstateAssets ?? {}) as EstateAssets;
    const ihtCalc = audit.derived?.iht_calc;
    const actionPack = audit.actionPack;
    const probateConfidence = audit.derived?.probate_confidence_level;

    return (
        <div>
            {Header}
            <main className="max-w-[960px] mx-auto px-8 py-12">
                {(!ihtCalc || !actionPack) ? (
                    <p className="text-[13px] text-ink3">
                        The audit ran but did not return a complete report — the wizard answers
                        may be incomplete. Open the audit to fill in any missing fields.
                    </p>
                ) : (
                    <>
                        <TrustEstateInclusionNotice ihtCalc={ihtCalc} />
                        <ReportBody
                            profile={profile}
                            assets={assets}
                            ihtCalc={ihtCalc}
                            actionPack={actionPack}
                            probateConfidence={probateConfidence}
                        />
                    </>
                )}
            </main>
        </div>
    );
};
